import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PREVIEW_PRICE_DISPLAY } from '../config/pricing';
import { useItineraryOffer } from 'alakai-aloha-shared/public-ui';
import {
  buildEventParamsFromAttribution,
  buildItineraryAttributionSearchString,
  extractItineraryAttributionFromLocation,
  saveItineraryAttribution,
  trackEvent,
} from '../utils/analytics';
import ItineraryWorkflow from '../components/homepage/ItineraryWorkflow';
import FeatureShowcase from '../components/common/FeatureShowcase';
import ItineraryAnatomy from '../components/itinerary/ItineraryAnatomy';
import WhyAlakai from '../components/common/WhyAlakai';
import { buildRuntimeCanonicalUrl } from '../lib/siteUrls';
import { getRuntimeSiteContext } from '../lib/siteContext';
import { getRuntimePlaybookBasePath } from '../lib/playbookRoutes';
import { useIslandHeroBackgroundImage } from '../components/homepage/useIslandHeroBackgroundImage';

function buildFaqs(islandConfig, pilotActive) {
  return [
    {
      q: 'I am splitting my trip between two regions. Do you support multi-stay itineraries?',
      a: 'Unfortunately no—our itinerary planner uses a single homebase location for each itinerary. We recommend generating two separate itineraries for your trip: one based in your first location, and the other based in your second location.'
    },
    {
      q: `We are visiting another island before/after our ${islandConfig.title} visit. Can you help with multi-island itineraries?`,
      a: `Unfortunately no—we are ${islandConfig.title} specialists through and through!`
    },
    {
      q: 'How long does the interview take?',
      a: 'You can get through it in as few as 3–4 minutes, but we recommend 8–12 minutes so your itinerary is built with the right context. The interview auto-saves, so you can pause and return. You can also share the interview with travel partners for review before you generate your itinerary.'
    },
    {
      q: 'What can I see before I commit?',
      a: pilotActive
        ? 'Your complete itinerary is free during our 30-day pilot. No credit card required. Start with a preview of your Arrival Day plus your first full day, then get the remaining days with a printable PDF, calendar export, sharing, and saving.'
        : `We generate a detailed itinerary for your Arrival Day plus your first full day so you can judge the fit. If you like what you see, pay ${PREVIEW_PRICE_DISPLAY} to unlock the remaining days and get a printable PDF, calendar export, share, and save.`
    },
    {
      q: "I don't know my exact travel dates yet. Can I still generate an itinerary?",
      a: 'Absolutely. Give us a sense for how long you want your trip to be and we will plan a date‑agnostic itinerary you can pin to exact days later.'
    },
    {
      q: "I haven't booked lodging / haven't decided where to stay. Can I still generate an itinerary?",
      a: "You bet! Every itinerary is anchored in a specific part of the island that we treat as your homebase. We don't need an exact property to plan your trip. If you need help deciding what area to stay in, we'll recommend a first and second choice, and you'll confirm before proceeding."
    },
    {
      q: 'What do you do with my info?',
      aPlain: 'We never sell your data, and you can request deletion at any time. See our Privacy Policy for more.',
      a: (
        <>
          We never sell your data, and you can request deletion at any time. See our{' '}
          <Link to="/privacy" className="underline text-emerald-300 hover:text-white">Privacy Policy</Link>{' '}for more.
        </>
      )
    }
  ];
}

function FaqSchema({ faqs }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: typeof f.a === 'string' ? f.a : (f.aPlain || '')
      }
    }))
  };

  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
  );
}

const SectionDivider = () => (
  <div className="max-w-6xl mx-auto pt-8 pb-8">
    <div className="border-t border-gray-600"></div>
  </div>
);


const KauaiItinerary = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { active: pilotActive, offer } = useItineraryOffer();
  const { islandConfig } = useMemo(() => getRuntimeSiteContext(), []);
  const playbookBasePath = useMemo(() => getRuntimePlaybookBasePath(), []);
  const faqs = useMemo(() => buildFaqs(islandConfig, pilotActive), [islandConfig, pilotActive]);
  const { currentHeroEntry, heroEntries } = useIslandHeroBackgroundImage(islandConfig.key, {
    surface: 'itinerary',
    storageNamespace: 'itinerary-hero',
  });
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const attributionTrackedRef = useRef(false);
  const initialAttribution = useMemo(
    () => extractItineraryAttributionFromLocation(location.search, location.pathname),
    [location.pathname, location.search]
  );
  const heroImageSrc = currentHeroEntry?.src || heroEntries[0]?.src;
  const heroImageAlt = currentHeroEntry?.alt || `${islandConfig.title} coastline`;

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 1024);
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!initialAttribution) {
      attributionTrackedRef.current = false;
      return;
    }

    const eventPayload = {
      ...buildEventParamsFromAttribution(initialAttribution),
      landing_path: initialAttribution.landingPath || playbookBasePath,
    };

    saveItineraryAttribution(initialAttribution);

    if (!attributionTrackedRef.current) {
      trackEvent('itinerary_hub_view', eventPayload);
      attributionTrackedRef.current = true;
    }
  }, [initialAttribution, playbookBasePath]);

  const startInterview = (label, labelText = null) => {
    const storedAttribution = initialAttribution
      ? {
          ...initialAttribution,
          cta_location: label,
          cta_label: labelText || label,
        }
      : {
          landingPath: playbookBasePath,
          cta_location: label,
          cta_label: labelText || label,
        };
    const eventPayload = initialAttribution
      ? {
          ...buildEventParamsFromAttribution(initialAttribution),
          landing_path: initialAttribution.landingPath || playbookBasePath,
          cta_location: label,
          cta_label: labelText || label,
          event_category: 'conversion',
          event_label: label,
        }
      : {
          event_category: 'conversion',
          event_label: label,
        };

    saveItineraryAttribution(storedAttribution);
    const attributionSearch = buildItineraryAttributionSearchString(location.search);
    trackEvent('interview_started', { ...eventPayload, ...(pilotActive && offer?.pilotId ? { pilot_id: offer.pilotId } : {}) });
    navigate(`/interview/wizard${attributionSearch}`);
  };

  return (
    <div className="text-white pb-12">
      <Helmet>
        <title>{`Custom ${islandConfig.title} Itinerary Planner | Alaka‘i Aloha`}</title>
        <meta
          name="description"
          content={pilotActive
            ? `Plan a personalized ${islandConfig.title} trip. Your complete itinerary is free during our 30-day pilot. No credit card required.`
            : `Plan a personalized ${islandConfig.title} trip. Complete a quick interview, preview your first 1.5 days, then unlock the full itinerary for ${PREVIEW_PRICE_DISPLAY}. Print, share, and save to your calendar.`}
        />
        <link rel="canonical" href={buildRuntimeCanonicalUrl(playbookBasePath)} />
      </Helmet>
      <FaqSchema faqs={faqs} />

      {/* Hero */}
      <section
        className="relative overflow-hidden h-[500px] md:h-[700px] lg:h-[875px]"
        style={isSmallScreen ? {
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          marginRight: 'calc(-50vw + 50%)'
        } : {}}
      >
        <div className="absolute inset-0 w-full h-full">
          {heroImageSrc ? (
            <img src={heroImageSrc} alt={heroImageAlt} className="w-full h-full object-cover" />
          ) : null}
          <div className="absolute inset-0 bg-black/30"></div>
        </div>

        <div className="relative z-10 flex items-center py-12 md:py-16 lg:py-20">
          <div className="max-w-6xl mx-auto px-4 w-full">
            <div className="text-center space-y-10 mt-8 lg:mt-12">
              <div className="space-y-6 max-w-3xl lg:max-w-4xl mx-auto">
                <h1
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-wide"
                  style={{ textShadow: '3px 3px 6px rgba(0,0,0,0.7), 1px 1px 3px rgba(0,0,0,0.8)' }}
                >
                  {`${islandConfig.title} Itineraries Built Around You`}
                </h1>
                <p
                  className="text-lg sm:text-xl md:text-2xl leading-relaxed text-white/95"
                  style={{ textShadow: '4px 4px 8px rgba(0,0,0,0.8), 2px 2px 4px rgba(0,0,0,0.9)' }}
                >
                  {pilotActive ? 'Your complete itinerary is free during our 30-day pilot. No credit card required.' : 'Make your vacation days and dollars count with a plan personalized to your travel preferences.'}
                </p>
              </div>

              <div className="flex justify-center">
                <button className="btn-primary-cta w-full sm:w-auto" onClick={() => startInterview(`${islandConfig.key}_itinerary_hero`, 'Plan Your Trip')}>
                  {pilotActive ? 'Plan My Trip Free' : 'Plan Your Trip'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider />

      <ItineraryWorkflow onPrimaryCtaClick={startInterview} ctaLocation="workflow_primary" />

      <SectionDivider />

      <FeatureShowcase
        title="What You Get"
        copy="Each unlocked itinerary comes with tools to keep your trip organized, easy to use, and easy to share."
        cta
        ctaLabel="Get Planning"
        onPrimaryCtaClick={startInterview}
        ctaLocation="what_you_get_cta"
        items={[
          {
            title: 'Print-ready PDF',
            body: 'Download or print a clean, one-day-per-page PDF for offline viewing. Easy to bring along or share with a travel partner.',
            image: {
              src: '/images/features/what-you-get/pdf-preview.png',
              alt: 'Preview of the itinerary PDF export'
            }
          },
          {
            title: 'Calendar export',
            body: 'Create calendar events (.ics) for every itinerary block. Adjust timing, set reminders, and invite others from the calendar you already use.',
            image: {
              src: '/images/features/what-you-get/calendar-export.png',
              alt: 'Calendar view generated from the itinerary'
            }
          },
          {
            title: 'Share & bookmark',
            body: 'Share a private link for feedback and bookmark the plan to keep everything synced across devices.',
            image: {
              src: '/images/features/what-you-get/share-bookmark.png',
              alt: 'Sharing and bookmarking options for the itinerary'
            }
          }
        ]}
      />

      <SectionDivider />

      <ItineraryAnatomy onPrimaryCtaClick={startInterview} ctaLocation="itinerary_anatomy_cta" />

      <SectionDivider />

      {/* Why Alaka‘i Aloha */}
      <WhyAlakai
        onPrimaryCtaClick={startInterview}
        ctaLocation="why_alakai_cta"
        items={[
          {
            icon: 'listen',
            title: 'We listen',
            body: 'Every itinerary starts with a thorough interview designed to truly understand you and your traveling group.',
            imageSrc: '/images/why-alakai/Interview.jpg'
          },
          {
            icon: 'bespoke',
            title: 'Bespoke itineraries',
            body: "Don't settle for off‑the‑rack templates built for the ‘average’ tourist.",
            imageSrc: '/images/why-alakai/Itinerary.jpg'
          },
            {
              icon: 'library',
              title: 'In‑depth profiles',
              body: `Every itinerary deep-links to the web's most comprehensive library of ${islandConfig.title} content.`,
              imageSrc: '/images/why-alakai/Profiles.jpg'
            }
        ]}
        proof={[
          { label: 'Restaurant profiles', to: '/restaurants', imageSrc: '/images/why-alakai/restaurant-profiles.jpg' },
          { label: 'Activity profiles', to: '/activities', imageSrc: '/images/why-alakai/Activity-profiles.jpg' },
          { label: 'Lodging profiles', to: '/lodging', imageSrc: '/images/why-alakai/Lodging-profiles.jpg' },
          { label: 'Region guide', to: '/regions', imageSrc: '/images/why-alakai/Region-guide.jpg' },
          { label: 'Area profiles', to: '/regions', imageSrc: '/images/why-alakai/Area-profiles.jpg' },
          { label: 'Blog', to: '/blog', imageSrc: '/images/why-alakai/Blog.jpg' },
        ]}
      />

      <SectionDivider />

      {/* FAQ */}
      <section>
        <h2 className="text-3xl md:text-4xl font-semibold text-center mb-6">FAQ</h2>
        <div className="max-w-3xl mx-auto px-4 space-y-3">
          {faqs.map(item => (
            <details key={item.q} className="group glass-card rounded-lg p-5">
              <summary className="cursor-pointer select-none font-medium text-white flex items-center justify-between gap-3">
                <span>{item.q}</span>
                <svg className="h-4 w-4 text-white/70 transition-transform group-open:rotate-180" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M12 15l-5-5h10l-5 5z" fill="currentColor" />
                </svg>
              </summary>
              <p className="mt-2 text-sm text-white/80 leading-relaxed">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <SectionDivider />

      {/* Final CTA */}
      <section>
        <div className="max-w-4xl mx-auto px-4 text-center space-y-3">
          <h2 className="text-3xl md:text-4xl font-semibold leading-tight">{`Ready to plan your ${islandConfig.shortTitle} trip?`}</h2>
          <div className="flex justify-center">
            <button className="btn-primary-cta w-full sm:w-auto" onClick={() => startInterview(`${islandConfig.key}_itinerary_final_cta`)}>
              {pilotActive ? 'Plan My Trip Free' : 'Start Interview'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KauaiItinerary;
